import { useCallback, useMemo, type ReactElement } from 'react';

import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { Outlet } from 'react-router';

import { useBoolean } from '@/_shared';

import { useBridgePlanContext } from '../bridge-plan';

import { AuditTrailTemplateV3 } from './audit-trail-template';
import {
  buildAuditTrailFileName,
  buildAuditTrailTimestamps,
  toDownloadFileName,
} from './audit-trail-template/timestamps';
import { AuditTrailContext } from './context';

export function AuditTrailProvider() {
  const loading = useBoolean();
  const bridgePlan = useBridgePlanContext();

  const getAuditTrailFile = useCallback(async () => {
    loading[1].setTrue();
    try {
      const {
        leadInfo: { email: lEmail, firstName, lastName },
        order_data: { auditLog: auditLogData, checkout },
      } = bridgePlan;

      const { termsSubSections, termsAcceptanceSystemInformation, customer } = auditLogData;
      const signerFirstName = firstName || checkout?.firstName || '';
      const signerLastName = lastName || checkout?.lastName || '';
      const signerName = [signerFirstName, signerLastName].filter(Boolean).join(' ');
      const signerEmail = lEmail || checkout?.email || '';
      const page4SignedAt = termsSubSections?.[3]?.signedDate;
      const { signedAt, sentAt, fileStamp } = buildAuditTrailTimestamps(page4SignedAt);
      const fileName = buildAuditTrailFileName(signerFirstName, signerLastName, fileStamp);
      const ipAddress = termsAcceptanceSystemInformation?.ipAddress || customer?.ipAddress || '';

      const auditBlob = await renderPdf(
        <AuditTrailTemplateV3
          signerName={signerName}
          signerEmail={signerEmail}
          fileName={fileName}
          signedAt={signedAt}
          sentAt={sentAt}
          ipAddress={ipAddress}
        />
      );

      saveAs(auditBlob, toDownloadFileName(fileName));
    } finally {
      loading[1].setFalse();
    }
  }, [bridgePlan, loading]);

  const value = useMemo(
    () => ({
      getAuditTrailFile,
      loading: loading[0],
      disabled: bridgePlan.isLoading,
      hasAuditTrail: !!bridgePlan.order_data?.auditLog,
    }),
    [getAuditTrailFile, loading, bridgePlan.isLoading, bridgePlan.order_data?.auditLog]
  );

  return (
    <AuditTrailContext.Provider value={value}>
      <Outlet />
    </AuditTrailContext.Provider>
  );
}

async function renderPdf(doc: ReactElement) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const asPdf = pdf([] as any);
  asPdf.updateContainer(doc);
  return asPdf.toBlob();
}
